// Order sets R1 (PROD-2723, v3 board 6 "ApAfter"): the A/P's orders NESTED UNDER EACH DIAGNOSIS.
// The PURE half — which orders and which set leftovers sit under which diagnosis. The component
// (`ap-dx-order-groups.tsx`) only renders it.
//
// Today's live A/P runs every order in one list beneath the diagnosis grid, so a set's leftovers
// ("Not ordered from <set>:" — optional items, items already on the visit, items no longer
// orderable) had nowhere to sit but one block per set under that whole list. Board 6 puts each
// diagnosis's orders under it and that set's leftovers at the foot of the same diagnosis. Behind
// `VITE_ORDER_SETS_APPLY` only; with the flag off the A/P never calls this.
//
// WHAT THIS READS. Only the apply RESULT: each application's target diagnosis (`dxId`) and its
// listed items, whatever rules produced them. It never decides what counts as a duplicate
// (PROD-2721 owns that matcher) — a new listing kind reaches the diagnosis as soon as
// `listedCountOf` counts it. The "No medication checks" notice (PROD-2721) follows the
// application's own `placesMedication` flag into the same card.
//
// EDUCATION (PROD-2850, board 6: "Living with type 2 diabetes · EDU · Article · sent after
// signing" inside the diabetes card). An article carries no diagnosis link on the wire (it is
// persisted top-level, Flutter parity), so an article a set attached is linked on the client to the
// diagnosis that set was applied to, as the set's orders are, and renders in that card after its
// orders. Hand-added articles, and a set's articles while its orders wait for a diagnosis, stay in
// the Education section. The A/P holds one article per title, so an article is placed once.
import type { DxCard, PlanOrder } from '../lib/to-patient-assessment';
import type { EducationArticle } from '../lib/to-education-article';
import type { AppliedOrderSet } from './use-order-set-apply';

/** An article a set attached, and the diagnosis that set was applied to. */
export interface ArticleDxLink {
  display: string;
  dxId: string;
}

/** An article nested under a diagnosis, with its position in the A/P's own article list. */
export interface NestedArticle {
  article: EducationArticle;
  /** Index in the `articles` passed in (what the remove handler takes when it has no id). */
  index: number;
}

/**
 * One diagnosis with the orders that justify it, the articles and the leftovers of the sets applied
 * to it.
 */
export interface DxOrderGroup {
  dx: DxCard;
  /** 1-based, the same numeral the diagnosis grid prints. */
  index: number;
  /** The orders placed under this diagnosis, in the plan's own order. */
  orders: PlanOrder[];
  /** Applications whose "Not ordered from <set>:" list sits under this diagnosis. */
  leftovers: AppliedOrderSet[];
  /**
   * Applications to this diagnosis that placed a medication: their "No medication checks" notice
   * (PROD-2721 AC1) sits in this card, beside the medications it is about.
   */
  notices: AppliedOrderSet[];
  /** Articles a set applied to this diagnosis attached (PROD-2850), after the orders. */
  articles: NestedArticle[];
}

export interface DxOrderGrouping {
  /** Diagnoses in canvas order; a diagnosis with no order, no article and no leftovers is left out. */
  groups: DxOrderGroup[];
  /** Orders linked to no diagnosis on the visit — listed after the groups, as today. */
  unlinked: PlanOrder[];
  /** The applications whose leftovers render inside a group (the canvas blocks skip them). */
  nestedAppKeys: ReadonlySet<string>;
  /** The applications whose medication notice renders inside a group (the canvas blocks skip it). */
  noticeAppKeys: ReadonlySet<string>;
  /** Indexes (in `articles`) of the articles that render inside a group; the Education section skips them. */
  nestedArticleIndexes: ReadonlySet<number>;
}

const normTitle = (v: string | null | undefined) => (v ?? '').trim().toLowerCase();

/** How many of an application's items are listed rather than added. */
export function listedCountOf(app: AppliedOrderSet): number {
  return app.optional.length + app.duplicates.length + app.notOrderable.length + app.unsupported.length;
}

/** True while an application has a leftovers list to show (not hidden, not undone). */
export function hasLeftovers(app: AppliedOrderSet): boolean {
  return !app.undoReceipt && !app.listHidden && listedCountOf(app) > 0;
}

/**
 * Place every order under ONE diagnosis: the first of its linked diagnoses in CANVAS order (the
 * primary leads when it is first). An order linked to several diagnoses is listed once — its row
 * still prints every linked code.
 *
 * DETERMINISTIC. Placement reads only the position of each diagnosis in `diagnoses` (the canvas's
 * own order, which only the clinician changes) — never the order of `orders`, of `order.dxIds`, or
 * of `applications` — so an order cannot move between cards unless the clinician reorders the
 * diagnoses or edits its links. Ties are impossible: positions are distinct. Inside a card the
 * orders keep the plan's order, and leftovers keep apply order (the hook appends each application). An application's leftovers go under the diagnosis it was applied
 * to; while its orders wait in "Needs a diagnosis" (no `dxId`), or after that diagnosis was
 * removed, they stay with the canvas blocks.
 */
export function groupOrdersByDiagnosis(
  diagnoses: readonly DxCard[],
  orders: readonly PlanOrder[],
  applications: readonly AppliedOrderSet[] = [],
  articles: readonly EducationArticle[] = [],
  articleLinks: readonly ArticleDxLink[] = [],
): DxOrderGrouping {
  const position = new Map(diagnoses.map((d, i) => [d.id, i]));
  const byDx = new Map<string, PlanOrder[]>();
  const unlinked: PlanOrder[] = [];
  for (const order of orders) {
    let best: number | undefined;
    for (const id of order.dxIds) {
      const at = position.get(id);
      if (at !== undefined && (best === undefined || at < best)) best = at;
    }
    if (best === undefined) {
      unlinked.push(order);
      continue;
    }
    const dxId = diagnoses[best].id;
    byDx.set(dxId, [...(byDx.get(dxId) ?? []), order]);
  }

  const leftoversByDx = new Map<string, AppliedOrderSet[]>();
  const noticesByDx = new Map<string, AppliedOrderSet[]>();
  const nestedAppKeys = new Set<string>();
  const noticeAppKeys = new Set<string>();
  for (const app of applications) {
    if (!app.dxId || !position.has(app.dxId) || app.undoReceipt) continue;
    if (hasLeftovers(app)) {
      leftoversByDx.set(app.dxId, [...(leftoversByDx.get(app.dxId) ?? []), app]);
      nestedAppKeys.add(app.key);
    }
    if (app.placesMedication) {
      noticesByDx.set(app.dxId, [...(noticesByDx.get(app.dxId) ?? []), app]);
      noticeAppKeys.add(app.key);
    }
  }

  // Each attached article under the diagnosis of the FIRST link naming its title (links keep apply
  // order), when that diagnosis is on the visit. Matched by title, as the A/P dedupes articles.
  const articlesByDx = new Map<string, NestedArticle[]>();
  const nestedArticleIndexes = new Set<number>();
  articles.forEach((article, index) => {
    const title = normTitle(article.display);
    if (!title) return;
    const link = articleLinks.find((l) => normTitle(l.display) === title && position.has(l.dxId));
    if (!link) return;
    articlesByDx.set(link.dxId, [...(articlesByDx.get(link.dxId) ?? []), { article, index }]);
    nestedArticleIndexes.add(index);
  });

  const groups: DxOrderGroup[] = [];
  diagnoses.forEach((dx, i) => {
    const dxOrders = byDx.get(dx.id) ?? [];
    const leftovers = leftoversByDx.get(dx.id) ?? [];
    const notices = noticesByDx.get(dx.id) ?? [];
    const dxArticles = articlesByDx.get(dx.id) ?? [];
    if (dxOrders.length === 0 && leftovers.length === 0 && dxArticles.length === 0) return;
    groups.push({ dx, index: i + 1, orders: dxOrders, leftovers, notices, articles: dxArticles });
  });
  // A notice whose diagnosis has no card (every order removed, nothing listed) stays with the
  // canvas blocks rather than vanishing.
  for (const key of [...noticeAppKeys]) {
    if (!groups.some((g) => g.notices.some((a) => a.key === key))) noticeAppKeys.delete(key);
  }
  return { groups, unlinked, nestedAppKeys, noticeAppKeys, nestedArticleIndexes };
}
